/**
 * UserPermissionsReport
 *
 * Full audit of every permission a user holds, showing:
 *  1. Summary table  — permission | category | granted via | scope | where
 *  2. Inheritance diagram — swimlane showing the exact chain:
 *       User → (direct role | group) → role → permissions × scope
 *
 * Route: /prioritization/settings/users/:userId/permissions
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, User, Users, Shield, Building2,
  ChevronRight, Search, Filter, Download, RefreshCw,
  CheckCircle, Circle, Layers,
} from 'lucide-react';
import { getSupabaseClient } from '../../../lib/supabaseClient';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { PERMISSION_DEFINITIONS } from '../../../types/prioritization/permissions';
import type { PermissionAction } from '../../../types/prioritization/permissions';
import { SponsoredVoicesSection } from '../../../features/voices/components/SponsoredVoicesSection';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserInfo {
  id: string;
  name: string;
  email: string;
}

interface GroupMembership {
  groupId: string;
  groupName: string;
}

interface RoleAssignment {
  roleId: string;
  roleName: string;
  roleDisplayName: string;
  subjectType: 'user' | 'group';
  subjectId: string;
  scopeKind: string;
  scopeId: string;
  active: boolean;
}

interface RolePermission {
  roleId: string;
  permissionKey: string;
}

interface OrgUnitPlacement {
  groupId: string;
  groupName: string;
  orgUnitId: string;
  orgUnitName: string;
  inheritable: boolean;
}

/** A fully resolved permission grant with its complete provenance chain */
interface ResolvedGrant {
  permission: string;
  label: string;
  category: string;
  description: string;
  /** 'direct' = role assigned straight to user; 'group' = via group membership */
  grantPath: 'direct' | 'group';
  roleName: string;
  roleDisplayName: string;
  groupName?: string;
  orgUnitName?: string;
  inheritable?: boolean;
  scopeKind: string;
  scopeId: string;
  scopeLabel: string;
}

/** A node in the swimlane diagram */
interface InheritanceNode {
  id: string;
  label: string;
  sublabel?: string;
  kind: 'user' | 'group' | 'role' | 'permission' | 'orgunit';
  children: InheritanceNode[];
  scopeLabel?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  items:    'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  process:  'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  tracks:   'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  session:  'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  pools:    'bg-cyan-100 text-cyan-700',
  criteria: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  values:   'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  attributes:'bg-lime-100 text-lime-700',
  catchball:'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  analytics:'bg-[var(--coral-dim)] text-[var(--coral)]',
  workflow: 'bg-fuchsia-100 text-fuchsia-700',
  decisions:'bg-[var(--coral-dim)] text-[var(--coral)]',
  admin:    'bg-[var(--coral-dim)] text-[var(--coral)]',
};

function categoryBadge(cat: string) {
  const cls = CATEGORY_COLORS[cat] ?? 'bg-[var(--hover-tint)] text-[var(--text-sec)]';
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${cls}`}>
      {cat}
    </span>
  );
}

function scopeBadge(scopeKind: string) {
  const map: Record<string, string> = {
    organization: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    resource:     'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    department:   'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
    team:         'bg-lime-100 text-lime-700',
    global:       'bg-[var(--hover-tint)] text-[var(--text-ter)]',
  };
  const cls = map[scopeKind] ?? 'bg-[var(--hover-tint)] text-[var(--text-sec)]';
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${cls}`}>
      {scopeKind}
    </span>
  );
}

// ─── Swimlane diagram ────────────────────────────────────────────────────────

const NODE_COLORS: Record<InheritanceNode['kind'], string> = {
  user:       'bg-[var(--slate)] text-white border-[var(--slate)]',
  group:      'bg-[var(--slate)] text-white border-[var(--slate)]',
  role:       'bg-[var(--mustard)] text-white border-[var(--mustard)]',
  permission: 'bg-[var(--green)] text-white border-[var(--green)]',
  orgunit:    'bg-[var(--surface)] text-white border-[var(--rule)]',
};

const NODE_ICONS: Record<InheritanceNode['kind'], React.ReactNode> = {
  user:       <User className="h-3 w-3" />,
  group:      <Users className="h-3 w-3" />,
  role:       <Shield className="h-3 w-3" />,
  permission: <CheckCircle className="h-3 w-3" />,
  orgunit:    <Building2 className="h-3 w-3" />,
};

function TreeNode({ node, depth = 0, last = false }: { node: InheritanceNode; depth?: number; last?: boolean }) {
  const [collapsed, setCollapsed] = useState(depth >= 3);
  const hasChildren = node.children.length > 0;

  return (
    <div className="relative">
      {/* Connector line from parent */}
      {depth > 0 && (
        <div
          className="absolute left-0 top-0 border-l-2 border-[var(--rule)]"
          style={{ height: last ? '1.25rem' : '100%', width: 1 }}
        />
      )}

      <div className="flex items-start gap-0" style={{ paddingLeft: depth * 20 }}>
        {/* Horizontal connector */}
        {depth > 0 && (
          <div className="mt-5 shrink-0 w-4 border-t-2 border-[var(--rule)]" />
        )}

        <div className="flex-1 min-w-0 mb-2">
          <div className="flex items-start gap-2">
            {/* Expand/collapse toggle */}
            {hasChildren ? (
              <button
                onClick={() => setCollapsed(c => !c)}
                className="mt-1 shrink-0 w-4 h-4 flex items-center justify-center text-[var(--text-ter)] hover:text-[var(--text-sec)]"
              >
                {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronRight className="h-3 w-3 rotate-90" />}
              </button>
            ) : (
              <span className="mt-1 shrink-0 w-4 h-4 flex items-center justify-center">
                <Circle className="h-2 w-2 text-[var(--text-ter)]" />
              </span>
            )}

            {/* Node pill */}
            <div className={`flex items-center gap-1.5 px-2.5 py-1  text-xs font-medium border ${NODE_COLORS[node.kind]}`}>
              {NODE_ICONS[node.kind]}
              <span className="truncate max-w-48">{node.label}</span>
            </div>

            {/* Sub-label & scope annotation */}
            {(node.sublabel || node.scopeLabel) && (
              <div className="flex flex-col justify-center gap-0.5 mt-0.5">
                {node.sublabel && (
                  <span className="text-xs text-[var(--text-ter)] italic">{node.sublabel}</span>
                )}
                {node.scopeLabel && (
                  <span className="text-xs bg-[var(--hover-tint)] text-[var(--text-sec)] px-1.5 py-0.5 rounded">
                    @ {node.scopeLabel}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Children */}
          {!collapsed && hasChildren && (
            <div className="relative ml-6 mt-1 border-l-2 border-[var(--rule)] pl-0">
              {node.children.map((child, i) => (
                <TreeNode
                  key={child.id}
                  node={child}
                  depth={0}
                  last={i === node.children.length - 1}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function UserPermissionsReport() {
  const { userId } = useParams<{ userId: string }>();
  const { currentWorkspaceId, currentTenantId } = useTenantStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [grants, setGrants] = useState<ResolvedGrant[]>([]);
  const [inheritanceTree, setInheritanceTree] = useState<InheritanceNode | null>(null);

  // Table UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [pathFilter, setPathFilter] = useState<'all' | 'direct' | 'group'>('all');
  const [activeTab, setActiveTab] = useState<'table' | 'diagram'>('table');

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchData = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);

    try {
      const supabase = getSupabaseClient();

      // 1. User info
      const { data: userData } = await supabase
        .from('users')
        .select('id, name, email')
        .eq('id', userId)
        .single();
      if (!userData) throw new Error('User not found');
      setUserInfo({ id: userData.id, name: userData.name || userData.email, email: userData.email });

      // 2. Group memberships — scoped to current workspace to prevent cross-workspace contamination
      const { data: membershipsData } = await supabase
        .from('memberships')
        .select('group_id, groups:group_id(id, name, workspace_id)')
        .eq('user_id', userId);
      const memberships: GroupMembership[] = (membershipsData || [])
        .filter((m: any) => {
          const gws = m.groups?.workspace_id;
          // Include groups that belong to the current workspace, or global groups (null workspace)
          return !gws || gws === currentWorkspaceId || gws === currentTenantId;
        })
        .map((m: any) => ({
          groupId: m.group_id,
          groupName: m.groups?.name || m.group_id,
        }));
      const groupIds = memberships.map(m => m.groupId);

      // 3. Role assignments: direct to user + via all groups the user belongs to.
      // No scope filter at the query level — we scope down client-side after building
      // resolved grants so we don't accidentally drop legitimate workspace assignments
      // (which may carry scope_kind='resource', 'workspace', or 'global').
      const raFilter = groupIds.length > 0
        ? supabase.from('role_assignments').select('*, roles:role_id(id, name, display_name)')
            .or(`subject_id.eq.${userId},subject_id.in.(${groupIds.join(',')})`)
            .eq('active', true)
        : supabase.from('role_assignments').select('*, roles:role_id(id, name, display_name)')
            .eq('subject_id', userId).eq('subject_type', 'user')
            .eq('active', true);

      const { data: raData } = await raFilter;
      const roleAssignments: RoleAssignment[] = (raData || []).map((ra: any) => ({
        roleId: ra.role_id,
        roleName: ra.roles?.name || ra.role_id,
        roleDisplayName: ra.roles?.display_name || ra.roles?.name || ra.role_id,
        subjectType: ra.subject_type,
        subjectId: ra.subject_id,
        scopeKind: ra.scope_kind || 'global',
        scopeId: ra.scope_id || '*',
        active: ra.active !== false,
      }));

      const roleIds = [...new Set(roleAssignments.map(ra => ra.roleId))];

      // 4. Permissions for each role
      // NOTE: DB column is "permission", not "permission_key"
      const { data: permData } = roleIds.length > 0
        ? await supabase.from('role_permissions').select('role_id, permission').in('role_id', roleIds)
        : { data: [] };
      const rolePermissions: RolePermission[] = (permData || []).map((p: any) => ({
        roleId: p.role_id,
        permissionKey: p.permission,
      }));

      // 5. Org unit placements for groups
      let orgUnitPlacements: OrgUnitPlacement[] = [];
      if (groupIds.length > 0) {
        const { data: placementData } = await supabase
          .from('group_node_assignments')
          .select('group_id, org_unit_id, inheritable, org_units:org_unit_id(id, name, display_name)')
          .in('group_id', groupIds)
          .eq('active', true);
        orgUnitPlacements = (placementData || []).map((p: any) => ({
          groupId: p.group_id,
          groupName: memberships.find(m => m.groupId === p.group_id)?.groupName || p.group_id,
          orgUnitId: p.org_unit_id,
          orgUnitName: p.org_units?.display_name || p.org_units?.name || p.org_unit_id,
          inheritable: p.inheritable,
        }));
      }

      // 6. Resolve scope labels
      const scopeIdToLabel = new Map<string, string>();
      if (currentWorkspaceId) scopeIdToLabel.set(currentWorkspaceId, 'This Workspace');
      if (currentTenantId)   scopeIdToLabel.set(currentTenantId, 'This Organization');
      scopeIdToLabel.set('*', 'All');

      const getScopeLabel = (kind: string, id: string) => {
        if (scopeIdToLabel.has(id)) return scopeIdToLabel.get(id)!;
        if (kind === 'organization') return 'Organization';
        if (kind === 'resource') return 'Workspace';
        return id.slice(0, 8) + '…';
      };

      // ── Build resolved grants ──────────────────────────────────────────────

      const resolvedGrants: ResolvedGrant[] = [];

      for (const ra of roleAssignments) {
        const permsForRole = rolePermissions.filter(rp => rp.roleId === ra.roleId);
        const scopeLabel = getScopeLabel(ra.scopeKind, ra.scopeId);

        if (ra.subjectType === 'user') {
          // Direct assignment
          for (const rp of permsForRole) {
            const def = PERMISSION_DEFINITIONS[rp.permissionKey as PermissionAction];
            resolvedGrants.push({
              permission: rp.permissionKey,
              label: def?.label ?? rp.permissionKey,
              category: def?.category ?? 'other',
              description: def?.description ?? '',
              grantPath: 'direct',
              roleName: ra.roleName,
              roleDisplayName: ra.roleDisplayName,
              scopeKind: ra.scopeKind,
              scopeId: ra.scopeId,
              scopeLabel,
            });
          }
        } else {
          // Via group
          const group = memberships.find(m => m.groupId === ra.subjectId);
          const groupName = group?.groupName ?? ra.subjectId;
          const placement = orgUnitPlacements.find(p => p.groupId === ra.subjectId);

          for (const rp of permsForRole) {
            const def = PERMISSION_DEFINITIONS[rp.permissionKey as PermissionAction];
            resolvedGrants.push({
              permission: rp.permissionKey,
              label: def?.label ?? rp.permissionKey,
              category: def?.category ?? 'other',
              description: def?.description ?? '',
              grantPath: 'group',
              roleName: ra.roleName,
              roleDisplayName: ra.roleDisplayName,
              groupName,
              orgUnitName: placement?.orgUnitName,
              inheritable: placement?.inheritable,
              scopeKind: ra.scopeKind,
              scopeId: ra.scopeId,
              scopeLabel,
            });
          }
        }
      }

      // Client-side workspace scoping: show only grants that are globally applicable
      // or explicitly scoped to this workspace / tenant. This prevents cross-workspace
      // contamination without needing fragile OR-chain scope filters in the DB query.
      const relevantGrants = currentWorkspaceId
        ? resolvedGrants.filter(g =>
            g.scopeId === '*' ||
            g.scopeKind === 'global' ||
            g.scopeId === currentWorkspaceId ||
            (currentTenantId && g.scopeId === currentTenantId)
          )
        : resolvedGrants;
      setGrants(relevantGrants);

      // ── Build inheritance tree ─────────────────────────────────────────────

      const userNode: InheritanceNode = {
        id: 'user',
        label: userData.name || userData.email,
        sublabel: userData.email,
        kind: 'user',
        children: [],
      };

      // Direct roles subtree
      const directRAs = roleAssignments.filter(ra => ra.subjectType === 'user');
      if (directRAs.length > 0) {
        const directNode: InheritanceNode = {
          id: 'direct',
          label: 'Direct Role Assignments',
          kind: 'role',
          children: directRAs.map(ra => ({
            id: `role-direct-${ra.roleId}-${ra.scopeId ?? 'null'}`,
            label: ra.roleDisplayName,
            kind: 'role' as const,
            scopeLabel: getScopeLabel(ra.scopeKind, ra.scopeId),
            children: rolePermissions
              .filter(rp => rp.roleId === ra.roleId)
              .map(rp => {
                const def = PERMISSION_DEFINITIONS[rp.permissionKey as PermissionAction];
                return {
                  id: `perm-direct-${ra.roleId}-${rp.permissionKey}`,
                  label: def?.label ?? rp.permissionKey,
                  sublabel: def?.category,
                  kind: 'permission' as const,
                  children: [],
                };
              }),
          })),
        };
        userNode.children.push(directNode);
      }

      // Groups subtree
      const groupedByGroup = new Map<string, RoleAssignment[]>();
      roleAssignments
        .filter(ra => ra.subjectType === 'group')
        .forEach(ra => {
          const list = groupedByGroup.get(ra.subjectId) || [];
          list.push(ra);
          groupedByGroup.set(ra.subjectId, list);
        });

      for (const [gId, ras] of groupedByGroup) {
        const groupMembership = memberships.find(m => m.groupId === gId);
        const groupName = groupMembership?.groupName ?? gId;
        const placement = orgUnitPlacements.find(p => p.groupId === gId);

        const groupNode: InheritanceNode = {
          id: `group-${gId}`,
          label: groupName,
          kind: 'group',
          children: [],
        };

        if (placement) {
          const ouNode: InheritanceNode = {
            id: `ou-${placement.orgUnitId}`,
            label: placement.orgUnitName,
            sublabel: placement.inheritable ? 'inheritable ↓' : 'this node only',
            kind: 'orgunit',
            children: [],
          };
          groupNode.children.push(ouNode);
        }

        for (const ra of ras) {
          const roleNode: InheritanceNode = {
            id: `role-group-${gId}-${ra.roleId}`,
            label: ra.roleDisplayName,
            kind: 'role',
            scopeLabel: getScopeLabel(ra.scopeKind, ra.scopeId),
            children: rolePermissions
              .filter(rp => rp.roleId === ra.roleId)
              .map(rp => {
                const def = PERMISSION_DEFINITIONS[rp.permissionKey as PermissionAction];
                return {
                  id: `perm-group-${gId}-${ra.roleId}-${rp.permissionKey}`,
                  label: def?.label ?? rp.permissionKey,
                  sublabel: def?.category,
                  kind: 'permission' as const,
                  children: [],
                };
              }),
          };
          groupNode.children.push(roleNode);
        }

        userNode.children.push(groupNode);
      }

      setInheritanceTree(userNode);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (userId && currentWorkspaceId) fetchData(); }, [userId, currentWorkspaceId]);

  // ── Derived state ──────────────────────────────────────────────────────────

  const categories = useMemo(
    () => ['all', ...new Set(grants.map(g => g.category))].sort(),
    [grants]
  );

  const filteredGrants = useMemo(() => {
    return grants.filter(g => {
      if (pathFilter !== 'all' && g.grantPath !== pathFilter) return false;
      if (categoryFilter !== 'all' && g.category !== categoryFilter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        if (
          !g.label.toLowerCase().includes(q) &&
          !g.permission.toLowerCase().includes(q) &&
          !g.description.toLowerCase().includes(q) &&
          !(g.roleName ?? '').toLowerCase().includes(q) &&
          !(g.groupName ?? '').toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [grants, searchQuery, categoryFilter, pathFilter]);

  // Deduplicate for the "unique permissions" count
  const uniquePermissions = useMemo(
    () => new Set(grants.map(g => g.permission)).size,
    [grants]
  );

  // ── Stats cards ────────────────────────────────────────────────────────────

  const stats = useMemo(() => ({
    total: grants.length,
    unique: uniquePermissions,
    direct: grants.filter(g => g.grantPath === 'direct').length,
    viaGroup: grants.filter(g => g.grantPath === 'group').length,
    roles: new Set(grants.map(g => g.roleName)).size,
    groups: new Set(grants.filter(g => g.groupName).map(g => g.groupName!)).size,
  }), [grants, uniquePermissions]);

  // ── CSV export ─────────────────────────────────────────────────────────────

  const handleExport = () => {
    const rows = [
      ['Permission', 'Label', 'Category', 'Granted Via', 'Role', 'Group', 'Org Unit', 'Scope', 'Scope Location'],
      ...grants.map(g => [
        g.permission, g.label, g.category,
        g.grantPath === 'direct' ? 'Direct' : 'Group',
        g.roleDisplayName, g.groupName ?? '', g.orgUnitName ?? '',
        g.scopeKind, g.scopeLabel,
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `permissions-${userInfo?.email ?? userId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-[var(--text-ter)] mx-auto mb-3" />
          <p className="text-sm text-[var(--text-ter)]">Loading permission report…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-6 text-center">
        <p className="text-[var(--coral)] font-medium">{error}</p>
        <Link to="/prioritization/settings/users" className="mt-4 inline-flex items-center gap-1 text-sm text-[var(--text-ter)] hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to Users
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-6 space-y-6">

      {/* ── Back + Header ── */}
      <div>
        <Link
          to="/prioritization/settings/users"
          className="inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Users
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-[var(--slate-dim)] flex items-center justify-center text-[var(--text-sec)] text-xl font-bold shrink-0">
              {(userInfo?.name ?? '?')[0].toUpperCase()}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[var(--text-pri)]">{userInfo?.name}</h1>
              <p className="text-sm text-[var(--text-ter)]">{userInfo?.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchData}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)]"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-white bg-[var(--slate)] hover:bg-[var(--slate)]"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
        </div>

        {userId && (
          <div className="mt-4">
            <SponsoredVoicesSection
              workspaceId={currentWorkspaceId}
              userId={userId}
            />
          </div>
        )}
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total grants', value: stats.total, icon: CheckCircle, color: 'text-[var(--text-ter)]' },
          { label: 'Unique permissions', value: stats.unique, icon: Shield, color: 'text-[var(--text-ter)]' },
          { label: 'Direct grants', value: stats.direct, icon: User, color: 'text-[var(--green)]' },
          { label: 'Via groups', value: stats.viaGroup, icon: Users, color: 'text-[var(--mustard)]' },
          { label: 'Roles held', value: stats.roles, icon: Layers, color: 'text-[var(--text-ter)]' },
          { label: 'Groups', value: stats.groups, icon: Building2, color: 'text-[var(--coral)]' },
        ].map(s => (
          <div key={s.label} className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 flex flex-col items-center text-center">
            <s.icon className={`h-5 w-5 mb-1 ${s.color}`} />
            <span className="text-2xl font-bold text-[var(--text-pri)]">{s.value}</span>
            <span className="text-xs text-[var(--text-ter)] mt-0.5">{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div className="flex border-b border-[var(--rule)] gap-1">
        {(['table', 'diagram'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-medium  transition-colors ${
              activeTab === tab
                ? 'bg-[var(--bg-raised)] border border-b-white border-[var(--rule)] text-[var(--text-ter)] -mb-px'
                : 'text-[var(--text-ter)] hover:text-[var(--text-sec)]'
            }`}
          >
            {tab === 'table' ? 'Permissions Table' : 'Inheritance Diagram'}
          </button>
        ))}
      </div>

      {/* ── Table tab ── */}
      {activeTab === 'table' && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 p-4 border-b border-[var(--rule)] bg-[var(--surface)]">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search permissions…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-[var(--text-ter)]" />
              <select
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] text-sm border border-[var(--rule)] px-2 py-2 focus:outline-none"
              >
                {categories.map(c => (
                  <option key={c} value={c}>{c === 'all' ? 'All categories' : c}</option>
                ))}
              </select>
              <select
                value={pathFilter}
                onChange={e => setPathFilter(e.target.value as typeof pathFilter)}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] text-sm border border-[var(--rule)] px-2 py-2 focus:outline-none"
              >
                <option value="all">All paths</option>
                <option value="direct">Direct only</option>
                <option value="group">Via group</option>
              </select>
            </div>

            <span className="text-xs text-[var(--text-ter)] ml-auto">
              {filteredGrants.length} of {grants.length} grants
            </span>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[var(--rule)]">
              <thead className="bg-[var(--surface)] text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left">Permission</th>
                  <th className="px-4 py-3 text-left">Category</th>
                  <th className="px-4 py-3 text-left">Granted via</th>
                  <th className="px-4 py-3 text-left">Role</th>
                  <th className="px-4 py-3 text-left">Scope type</th>
                  <th className="px-4 py-3 text-left">Where</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--rule)] text-sm">
                {filteredGrants.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-[var(--text-ter)] italic">
                      No permissions match the current filters.
                    </td>
                  </tr>
                ) : (
                  filteredGrants.map((g, i) => (
                    <tr key={i} className="hover:bg-[var(--surface)] transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-[var(--text-pri)]">{g.label}</div>
                        <div className="text-xs text-[var(--text-ter)] mt-0.5">{g.permission}</div>
                        {g.description && (
                          <div className="text-xs text-[var(--text-ter)] mt-0.5 max-w-xs truncate" title={g.description}>
                            {g.description}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">{categoryBadge(g.category)}</td>
                      <td className="px-4 py-3">
                        {g.grantPath === 'direct' ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] px-2 py-0.5 rounded-full">
                            <User className="h-3 w-3" /> Direct
                          </span>
                        ) : (
                          <div className="flex flex-col gap-0.5">
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] px-2 py-0.5 rounded-full w-fit">
                              <Users className="h-3 w-3" /> {g.groupName}
                            </span>
                            {g.orgUnitName && (
                              <span className="inline-flex items-center gap-1 text-xs text-[var(--text-sec)] bg-[var(--surface)] px-2 py-0.5 rounded-full w-fit">
                                <Building2 className="h-3 w-3" />
                                {g.orgUnitName}
                                {g.inheritable && <span className="text-[var(--text-ter)]">↓</span>}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-medium text-[var(--mustard)] bg-[var(--mustard-dim)] px-2 py-0.5 rounded-full">
                          {g.roleDisplayName}
                        </span>
                      </td>
                      <td className="px-4 py-3">{scopeBadge(g.scopeKind)}</td>
                      <td className="px-4 py-3 text-sm text-[var(--text-sec)]">{g.scopeLabel}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Diagram tab ── */}
      {activeTab === 'diagram' && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          <div className="mb-4 flex items-center gap-6 text-xs text-[var(--text-ter)] flex-wrap">
            <span className="font-semibold text-[var(--text-sec)]">Legend:</span>
            {(['user', 'group', 'role', 'permission', 'orgunit'] as const).map(k => (
              <span key={k} className="flex items-center gap-1.5">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5  text-xs font-medium border ${NODE_COLORS[k]}`}>
                  {NODE_ICONS[k]}
                  <span className="capitalize">{k === 'orgunit' ? 'Org Unit' : k}</span>
                </span>
              </span>
            ))}
            <span className="ml-auto text-[var(--text-ter)]">Click node label to expand/collapse</span>
          </div>

          <div className="border-t border-[var(--rule)] pt-4">
            {inheritanceTree ? (
              <div className="overflow-x-auto">
                <div className="min-w-[600px]">
                  <TreeNode node={inheritanceTree} />
                </div>
              </div>
            ) : (
              <p className="text-sm text-[var(--text-ter)] italic py-8 text-center">
                No inheritance data available.
              </p>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
